/**
 * Which pricing response is still the one the panel wants.
 *
 * A basket can be repriced faster than the network answers, so responses arrive out of order and
 * some arrive for a basket that no longer exists. A request takes a ticket when it starts and
 * presents it when it returns; only the newest ticket is honoured.
 *
 * Claiming is the single point that supersedes earlier work — callers must not advance the
 * counter themselves, which is what previously stranded the spinner: an invalidation moved the
 * target after a request had taken its ticket, so no response could ever match.
 */
export type PriceSequence = {
  /** Takes the ticket for a request about to start, superseding any still outstanding. */
  claim: () => number;
  /** True when this response is the newest one asked for and should be rendered. */
  isCurrent: (ticket: number) => boolean;
  /** Records that this response arrived, so an outstanding request is no longer awaited. */
  settle: (ticket: number) => void;
  /** Discards outstanding work without asking for more, for a basket that cannot be priced. */
  invalidate: () => void;
  /** True while a claimed request has neither settled nor been invalidated. */
  hasOutstanding: () => boolean;
};

export const createPriceSequence = (): PriceSequence => {
  let latest = 0;
  let outstanding = false;

  return {
    claim: () => {
      latest += 1;
      outstanding = true;
      return latest;
    },
    isCurrent: (ticket: number) => ticket === latest && outstanding,
    settle: (ticket: number) => {
      if (ticket === latest) {
        outstanding = false;
      }
    },
    invalidate: () => {
      latest += 1;
      outstanding = false;
    },
    hasOutstanding: () => outstanding,
  };
};
